import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBankBalances } from "redux/admin/bank-balances/bankBalancesSlice";
import { FaUniversity, FaDollarSign, FaBitcoin, FaCalculator } from "react-icons/fa";
import { GiReceiveMoney } from "react-icons/gi";

const BankBalancesContainer = () => {
  const dispatch = useDispatch();
  const { balances, isLoading, isError, message } = useSelector((state) => state.adminBankBalances);

  useEffect(() => {
    dispatch(getBankBalances());
  }, [dispatch]);

  if (isLoading) {
    return (
      <div className="bg-black min-h-screen flex items-center justify-center">
        <div className="text-white-100 text-xl">Bakiyeler yükleniyor...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-black min-h-screen flex items-center justify-center">
        <div className="text-red-500 text-xl">Hata: {message}</div>
      </div>
    );
  }

  const {
    bank_balances = [],
    reseller_balances = 0,
    crypto_balances = 0,
    usdttry_rate = 0
  } = balances;

  // Calculations
  const cryptoTryEquivalent = Math.round(crypto_balances * usdttry_rate, 2);
  const totalBankBalances = bank_balances.reduce((sum, bank) => sum + bank.balance, 0) + cryptoTryEquivalent;
  const netBalance = totalBankBalances - reseller_balances;

  return (
    <div className="bg-black min-h-screen">
      <div className="p-4">
        <h1 className="text-3xl font-bold text-white-100 mb-6">Banka Bakiyeleri</h1>
        
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          {/* Reseller Balances */}
          <div className="bg-gray-900 flex justify-between w-full p-4 rounded-lg">
            <div className="flex items-center gap-4 rounded-lg p-6">
              <span className="rounded-full bg-primary p-3 text-white-100">
                <GiReceiveMoney size={30} />
              </span>
              <div>
                <p className="text-2xl font-medium text-white-100">
                  {reseller_balances.toLocaleString('tr-TR', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })}₺
                </p>
                <p className="text-base text-white-100">Bayi Bakiyesi</p>
              </div>
            </div>
          </div>

          {/* Dynamic Bank Balances */}
          {bank_balances.map((bank, index) => (
            <div key={index} className="bg-gray-900 flex justify-between w-full p-4 rounded-lg">
              <div className="flex items-center gap-4 rounded-lg p-6">
                <span className="rounded-full bg-primary p-3 text-white-100">
                  <FaUniversity size={30} />
                </span>
                <div>
                  <p className="text-2xl font-medium text-white-100">
                    {bank.balance.toLocaleString('tr-TR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}₺
                  </p>
                  <p className="text-base text-white-100">{bank.bank_name} Bakiyesi</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          {/* Crypto Balance */}
          <div className="bg-gray-900 flex justify-between w-full p-4 rounded-lg">
            <div className="flex items-center gap-4 rounded-lg p-6">
              <span className="rounded-full bg-primary p-3 text-white-100">
                <FaBitcoin size={30} />
              </span>
              <div>
                <p className="text-2xl font-medium text-white-100">
                  {crypto_balances.toLocaleString('en-US', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })} USDT
                </p>
                <p className="text-xs text-white-100">
                  ≈ {cryptoTryEquivalent.toLocaleString('tr-TR', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })}₺
                </p>
                <p className="text-base text-white-100">Kripto Bakiyesi</p>
              </div>
            </div>
          </div>

          {/* USDT/TRY Rate */}
          <div className="bg-gray-900 flex justify-between w-full p-4 rounded-lg">
            <div className="flex items-center gap-4 rounded-lg p-6">
              <span className="rounded-full bg-primary p-3 text-white-100">
                <FaDollarSign size={30} />
              </span>
              <div>
                <p className="text-2xl font-medium text-white-100">
                  {usdttry_rate.toLocaleString('tr-TR', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })}₺
                </p>
                <p className="text-base text-white-100">USDT/TRY Kuru</p>
              </div>
            </div>
          </div>

          {/* Net Balance */}
          <div className="bg-gray-900 flex justify-between w-full p-4 rounded-lg border-2 border-yellow-500">
            <div className="flex items-center gap-4 rounded-lg p-6">
              <span className="rounded-full bg-yellow-500 p-3 text-white-100">
                <FaCalculator size={30} />
              </span>
              <div>
                <p className={`text-2xl font-medium ${netBalance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {netBalance.toLocaleString('tr-TR', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })}₺
                </p>
                <p className="text-xs text-white-100">
                  ({bank_balances.map(bank => bank.bank_name).join(' + ')}{bank_balances.length > 0 ? ' + ' : ''}Kripto TRY) - Bayi
                </p>
                <p className="text-base text-white-100">Net Bakiye</p>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div className="bg-gray-800 p-6 rounded-lg">
          <h2 className="text-xl font-bold text-white-100 mb-4">Özet</h2>
          <div className="grid lg:grid-cols-2 gap-4 text-white-100">
            <div>
              <p className="text-sm opacity-75">Toplam Banka Bakiyeleri:</p>
              {bank_balances.map((bank, index) => (
                <p key={index} className="text-lg">
                  {bank.bank_name}: {bank.balance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}₺
                </p>
              ))}
              <p className="text-lg">
                Kripto (TRY): {cryptoTryEquivalent.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}₺
              </p>
              <hr className="my-2 border-gray-600" />
              <p className="text-lg font-semibold">
                Toplam: {totalBankBalances.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}₺
              </p>
            </div>
            <div>
              <p className="text-sm opacity-75">Net Hesaplama:</p>
              <p className="text-lg">
                Bayi: {reseller_balances.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}₺
              </p>
              <p className="text-lg">
                Banka Toplamı: {totalBankBalances.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}₺
              </p>
              <hr className="my-2 border-gray-600" />
              <p className={`text-lg font-semibold ${netBalance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                Net: {netBalance.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}₺
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BankBalancesContainer; 